"use client";

/**
 * Bandeau de consentement + pages vues (voir `lib/analytics.ts`).
 *
 * - Sans ID de mesure : ne rend rien.
 * - Choix déjà fait (moins de 13 mois) : pas de bandeau ; si « accepté », la
 *   mesure se charge.
 * - Sinon : bandeau. « Refuser » et « Accepter » ont le MÊME poids visuel
 *   (exigence CNIL : refuser doit être aussi simple qu'accepter).
 * - « Gérer les cookies » (pied de page, politique de confidentialité) le
 *   rouvre via `rouvrirChoixCookies`.
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  EVENEMENT_ROUVRIR,
  GA_ID,
  chargerGa,
  cheminExclu,
  couperGa,
  enregistrerChoix,
  lireChoix,
  pageVue,
} from "@/lib/analytics";

export default function MesureAudience() {
  const chemin = usePathname() ?? "/";
  const [ouvert, setOuvert] = useState(false);

  // Premier passage : choix enregistré ou bandeau.
  useEffect(() => {
    if (!GA_ID) return;
    const choix = lireChoix();
    if (choix === "accepte") chargerGa();
    // Différé : pas de setState synchrone dans l'effet de montage.
    const t = setTimeout(() => {
      if (choix === null && !cheminExclu(location.pathname)) setOuvert(true);
    }, 0);
    const rouvrir = () => setOuvert(true);
    window.addEventListener(EVENEMENT_ROUVRIR, rouvrir);
    return () => {
      clearTimeout(t);
      window.removeEventListener(EVENEMENT_ROUVRIR, rouvrir);
    };
  }, []);

  // Une page vue par changement de chemin (adresse nettoyée).
  useEffect(() => {
    if (!GA_ID) return;
    // Laisse au titre de la page le temps d'être posé.
    const t = setTimeout(() => pageVue(chemin), 50);
    return () => clearTimeout(t);
  }, [chemin]);

  if (!GA_ID || !ouvert) return null;

  const decider = (accepte: boolean) => {
    enregistrerChoix(accepte ? "accepte" : "refuse");
    if (accepte) {
      chargerGa();
      pageVue(chemin);
    } else {
      couperGa();
    }
    setOuvert(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Mesure d'audience"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-xl rounded-xl border border-border bg-background/95 p-3 text-foreground shadow-2xl backdrop-blur sm:inset-x-auto sm:left-4 sm:mx-0 sm:p-4"
      style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <p className="text-sm font-semibold">Mesure d&apos;audience</p>
      {/* Au téléphone, le texte complet couvrait la moitié de l'écran. La version
          courte garde ce que le consentement exige d'annoncer — la finalité,
          l'outil, l'absence de publicité — et renvoie au détail. */}
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:hidden">
        Avec votre accord, nous mesurons l&apos;audience avec Google Analytics — jamais le contenu de vos
        projets, aucun usage publicitaire.{" "}
        <Link href="/legal/confidentialite#cookies" className="underline underline-offset-2 hover:text-foreground">
          En savoir plus
        </Link>
      </p>
      <p className="mt-1 hidden text-xs leading-relaxed text-muted-foreground sm:block">
        Avec votre accord, nous mesurons la fréquentation et l&apos;usage de Synoplix avec Google Analytics
        (pages vues, actions comme une inscription ou un export — jamais le contenu de vos projets) pour
        l&apos;améliorer. Aucun usage publicitaire. Vous pouvez changer d&apos;avis à tout moment via « Gérer
        les cookies ».{" "}
        <Link href="/legal/confidentialite#cookies" className="underline underline-offset-2 hover:text-foreground">
          En savoir plus
        </Link>
      </p>
      <div className="mt-2.5 flex gap-2 sm:mt-3">
        <button
          type="button"
          onClick={() => decider(false)}
          className="flex-1 rounded-md border border-border bg-muted px-3 py-2 text-sm font-medium hover:bg-muted/70"
        >
          Refuser
        </button>
        <button
          type="button"
          onClick={() => decider(true)}
          className="flex-1 rounded-md border border-border bg-muted px-3 py-2 text-sm font-medium hover:bg-muted/70"
        >
          Accepter
        </button>
      </div>
    </div>
  );
}
